import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ArrowLeft, ArrowRight, TrendingUp, TrendingDown, Minus, Calendar, Info } from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrendsPage = () => {
    const { testName: urlTestName } = useParams();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const dateLocale = isRTL ? ar : enUS;
    const BackIcon = isRTL ? ArrowRight : ArrowLeft;
    
    const [availableTests, setAvailableTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(urlTestName || '');
    const [trendData, setTrendData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTrend, setLoadingTrend] = useState(false);

    useEffect(() => {
        const fetchAvailableTests = async () => {
            try {
                const response = await axios.get(`${API}/trends`);
                setAvailableTests(response.data.available_tests || []);
                if (!urlTestName && response.data.available_tests?.length > 0) {
                    setSelectedTest(response.data.available_tests[0]);
                }
            } catch (error) {
                console.error('Failed to fetch available tests:', error);
            } finally {
                setLoading(false);
            }
        };

        if (user) fetchAvailableTests();
    }, [user, urlTestName]);

    useEffect(() => {
        const fetchTrendData = async () => {
            if (!selectedTest) return;
            
            setLoadingTrend(true);
            try {
                const response = await axios.get(`${API}/trends/${encodeURIComponent(selectedTest)}`);
                setTrendData(response.data.data || []);
            } catch (error) {
                console.error('Failed to fetch trend data:', error);
            } finally {
                setLoadingTrend(false);
            }
        };

        if (user && selectedTest) fetchTrendData();
    }, [user, selectedTest]);

    const getTrendDirection = () => {
        if (trendData.length < 2) return null;
        const latest = trendData[trendData.length - 1].value;
        const previous = trendData[trendData.length - 2].value;
        if (latest > previous) return 'up';
        if (latest < previous) return 'down';
        return 'stable';
    };

    const getChartData = () => {
        return trendData.map(item => ({
            ...item,
            date: format(new Date(item.date), 'MMM d', { locale: dateLocale }),
            fullDate: format(new Date(item.date), 'MMM d, yyyy', { locale: dateLocale })
        }));
    };

    const getReferenceValues = () => {
        if (trendData.length === 0) return { low: null, high: null };
        const range = trendData[0].reference_range;
        if (!range) return { low: null, high: null };
        const parts = range.split('-').map(p => parseFloat(p.trim()));
        return { low: parts[0] || null, high: parts[1] || null };
    };

    const trend = getTrendDirection();
    const chartData = getChartData();
    const refValues = getReferenceValues();
    const unit = trendData[0]?.unit || '';

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Skeleton className="h-10 w-48 mb-6" />
                <Skeleton className="h-96 rounded-2xl" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="trends-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Link to="/dashboard">
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
                        <BackIcon className="w-5 h-5" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-stone-800">{t('trends.title')}</h1>
                    <p className="text-stone-500">{t('trends.subtitle')}</p>
                </div>
            </div>

            {availableTests.length === 0 ? (
                <Card className="border-0 shadow-lg shadow-stone-200/50">
                    <CardContent className="p-12 text-center">
                        <div className="w-20 h-20 rounded-3xl bg-teal-50 flex items-center justify-center mx-auto mb-6">
                            <TrendingUp className="w-10 h-10 text-teal-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-stone-800 mb-2">{t('trends.noDataYet')}</h3>
                        <p className="text-stone-500 mb-6 max-w-md mx-auto">
                            {t('trends.noDataDesc')}
                        </p>
                        <Link to="/upload">
                            <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full">
                                {t('trends.uploadReport')}
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Test Selector */}
                    <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6" data-testid="test-selector-card">
                        <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="flex-1">
                                    <label className="text-sm font-medium text-stone-600 mb-2 block">{t('trends.selectTest')}</label>
                                    <Select value={selectedTest} onValueChange={setSelectedTest}>
                                        <SelectTrigger className="w-full sm:w-80 h-12 rounded-xl" data-testid="test-select">
                                            <SelectValue placeholder={t('trends.selectTest')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableTests.map((test) => (
                                                <SelectItem key={test} value={test}>
                                                    {test}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {trendData.length > 0 && (
                                    <div className="flex items-center gap-6">
                                        <div className="text-center">
                                            <p className="text-sm text-stone-500">{t('trends.latestValue')}</p>
                                            <p className="text-2xl font-bold text-stone-800">
                                                {trendData[trendData.length - 1]?.value} <span className="text-sm font-normal text-stone-500">{unit}</span>
                                            </p>
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm text-stone-500">{t('trends.trend')}</p>
                                            <div className="flex items-center justify-center gap-1">
                                                {trend === 'up' && <TrendingUp className="w-5 h-5 text-amber-600" />}
                                                {trend === 'down' && <TrendingDown className="w-5 h-5 text-emerald-600" />}
                                                {trend === 'stable' && <Minus className="w-5 h-5 text-stone-600" />}
                                                <span className={`font-semibold ${
                                                    trend === 'up' ? 'text-amber-600' : 
                                                    trend === 'down' ? 'text-emerald-600' : 
                                                    'text-stone-600'
                                                }`}>
                                                    {trend === 'up' ? t('trends.increasing') : trend === 'down' ? t('trends.decreasing') : t('trends.stable')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Chart */}
                    <Card className="border-0 shadow-lg shadow-stone-200/50 mb-6" data-testid="trend-chart-card">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-teal-600" />
                                {selectedTest} {t('trends.trend')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loadingTrend ? (
                                <Skeleton className="h-80 rounded-xl" />
                            ) : trendData.length === 0 ? (
                                <div className="h-80 flex items-center justify-center text-stone-500">
                                    <p>{t('trends.noDataAvailable')}</p>
                                </div>
                            ) : trendData.length === 1 ? (
                                <div className="h-80 flex flex-col items-center justify-center text-stone-500">
                                    <Calendar className="w-12 h-12 mb-4 text-stone-400" />
                                    <p className="text-lg font-medium text-stone-700">{t('trends.oneDataPoint')}</p>
                                    <p>{t('trends.uploadMore')}</p>
                                </div>
                            ) : (
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                                            <XAxis 
                                                dataKey="date" 
                                                stroke="#78716C"
                                                tick={{ fontSize: 12 }}
                                                reversed={isRTL}
                                            />
                                            <YAxis 
                                                stroke="#78716C"
                                                tick={{ fontSize: 12 }}
                                                domain={['auto', 'auto']}
                                                orientation={isRTL ? 'right' : 'left'}
                                            />
                                            <Tooltip 
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: 'none',
                                                    borderRadius: '12px',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                }}
                                                formatter={(value) => [`${value} ${unit}`, selectedTest]}
                                                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
                                            />
                                            {refValues.low && (
                                                <ReferenceLine 
                                                    y={refValues.low} 
                                                    stroke="#10B981" 
                                                    strokeDasharray="5 5"
                                                    label={{ value: t('report.low'), fill: '#10B981', fontSize: 11 }}
                                                />
                                            )}
                                            {refValues.high && (
                                                <ReferenceLine 
                                                    y={refValues.high} 
                                                    stroke="#F59E0B" 
                                                    strokeDasharray="5 5"
                                                    label={{ value: t('report.high'), fill: '#F59E0B', fontSize: 11 }}
                                                />
                                            )}
                                            <Line 
                                                type="monotone" 
                                                dataKey="value" 
                                                stroke="#008B8B" 
                                                strokeWidth={3}
                                                dot={{ r: 6, fill: '#008B8B', strokeWidth: 2, stroke: 'white' }}
                                                activeDot={{ r: 8, fill: '#008B8B', strokeWidth: 3, stroke: 'white' }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Reference Info */}
                    {trendData.length > 0 && (
                        <Card className="border-0 shadow-lg shadow-stone-200/50" data-testid="reference-info-card">
                            <CardContent className="p-6">
                                <div className="flex items-start gap-3">
                                    <Info className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <h4 className="font-medium text-stone-800 mb-1">{t('trends.referenceRange')}</h4>
                                        <p className="text-stone-600">
                                            {t('trends.normalRange')} {selectedTest}: <span className="font-semibold">{trendData[0]?.reference_range || 'N/A'}</span>
                                        </p>
                                        <p className="text-sm text-stone-500 mt-2">
                                            {t('trends.referenceNote')}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default TrendsPage;
